import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTrash, restoreTrashItem, permanentDeleteTrashItem, emptyTrash } from "../../api";
import { TrashEntry } from "../../types/requestsOutputs";
import Pagination from "../../components/common/Pagination";
import DeleteModal from "../../components/modals/DeleteModal";
import TextModal from "../../components/modals/ProjectTextModal";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Icon from "../../components/common/Icon";
import styles from "./Trash.module.css";
import audioFooter from "../../components/common/AudioPlayerFooter.module.css";

type TrashItemType = "audio" | "cartel" | "newsletter";
type KindFilter = "all" | "item" | "project";

interface TrashProps {
    itemType: TrashItemType;
    // Refreshes the App-level project list so the sidebar reflects restored projects.
    refreshProjects?: () => Promise<void>;
}

const PAGE_SIZE = 100;

// Compact relative time, localized via i18n interpolation ("il y a Xj" / "Xd ago").
const relativeTime = (dateStr: string, t: any): string => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days >= 7) return t("weeksAgo", { count: Math.floor(days / 7) });
    if (days >= 1) return t("daysAgo", { count: days });
    if (hours >= 1) return t("hoursAgo", { count: hours });
    return t("minutesAgo", { count: Math.max(mins, 1) });
};

const Trash: React.FC<TrashProps> = ({ itemType, refreshProjects }) => {
    const token = localStorage.getItem("token");
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [entries, setEntries] = useState<TrashEntry[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [counts, setCounts] = useState<{ items: number; projects: number }>({ items: 0, projects: 0 });
    const [query, setQuery] = useState<string>("");
    const [debouncedQuery, setDebouncedQuery] = useState<string>("");
    const [kindFilter, setKindFilter] = useState<KindFilter>("all");

    const [playing, setPlaying] = useState<TrashEntry | null>(null);
    const [textPreview, setTextPreview] = useState<TrashEntry | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<TrashEntry | null>(null);
    const [emptyOpen, setEmptyOpen] = useState<boolean>(false);

    // Debounce the search box.
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedQuery(query);
            setPage(1);
        }, 350);
        return () => clearTimeout(id);
    }, [query]);

    const fetchTrash = async () => {
        if (!token) return;
        try {
            const response = await getTrash({
                token,
                itemType,
                page,
                limit: PAGE_SIZE,
                query: debouncedQuery || undefined,
                kind: kindFilter === "all" ? undefined : kindFilter,
            });
            setEntries(response.entries || []);
            setTotalPages(response.totalPages || 1);
            setTotalCount(response.totalCount || 0);
            setCounts(response.counts || { items: 0, projects: 0 });
        } catch (error) {
            console.error("Error fetching trash:", error);
        }
    };

    useEffect(() => {
        fetchTrash();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, itemType, debouncedQuery, kindFilter]);

    const handleRestore = async (entry: TrashEntry) => {
        if (!token) return;
        try {
            await restoreTrashItem({ token, id: entry.id, kind: entry.kind, itemType });
            // Keep the sidebar project list in sync (restored project reappears).
            await refreshProjects?.();
            if (entries.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchTrash();
        } catch (error) {
            console.error("Failed to restore:", error);
        }
    };

    const handlePermanentDelete = async () => {
        if (!token || !deleteTarget) return;
        try {
            await permanentDeleteTrashItem({ token, id: deleteTarget.id, kind: deleteTarget.kind, itemType });
            setDeleteTarget(null);
            if (entries.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchTrash();
        } catch (error) {
            console.error("Failed to permanently delete:", error);
            setDeleteTarget(null);
        }
    };

    const handleEmptyTrash = async () => {
        if (!token) return;
        try {
            await emptyTrash({ token, itemType });
            setEmptyOpen(false);
            setPage(1);
            await fetchTrash();
        } catch (error) {
            console.error("Failed to empty trash:", error);
            setEmptyOpen(false);
        }
    };

    const renderRow = (entry: TrashEntry) => {
        const isProject = entry.kind === "project";
        const isAudio = !isProject && itemType === "audio";
        const lang = entry.language ? entry.language.toUpperCase() : "";

        return (
            <div key={`${entry.kind}-${entry.id}`} className={styles.row}>
                <div className={styles.rowLeft}>
                    {isProject ? (
                        <div className={`${styles.thumb} ${styles.thumbStatic}`}>
                            <Icon name="ic-folder" className={styles.thumbIcon} />
                        </div>
                    ) : isAudio && entry.audioUrl ? (
                        <div className={styles.thumb} onClick={() => setPlaying(entry)}>
                            <Icon name="ic-play" className={styles.thumbIcon} />
                        </div>
                    ) : (
                        <div className={`${styles.thumb} ${styles.thumbStatic}`}>
                            <Icon name="ic-file-text" className={styles.thumbIcon} />
                        </div>
                    )}
                    <div className={styles.info}>
                        <div className={styles.nameRow}>
                            <h4 className={styles.name}>{entry.name}</h4>
                            {isProject && <span className={styles.badge}>{t("projectBadge")}</span>}
                        </div>
                        <p className={styles.meta}>
                            {isProject
                                ? t("containsNItems", { count: entry.childCount || 0 })
                                : entry.voiceName || (entry.text ? entry.text.slice(0, 80) : "")}
                            {entry.deletedAt ? ` · ${relativeTime(entry.deletedAt, t)}` : ""}
                        </p>
                    </div>
                </div>

                <div className={styles.rowRight}>
                    {lang && <span className={styles.lang}>{lang}</span>}
                    {!isProject && entry.text && (
                        <Icon name="ic-file-text" className={styles.iconAction} onClick={() => setTextPreview(entry)} />
                    )}
                    <button type="button" className={styles.restoreBtn} onClick={() => handleRestore(entry)}>
                        {t("restore")}
                    </button>
                    <button
                        type="button"
                        className={styles.deleteIconBtn}
                        title={t("permanentDelete")}
                        onClick={() => setDeleteTarget(entry)}
                    >
                        <Icon name="ic-trash" className={styles.deleteIcon} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>
                        {t("trash")} ({totalCount})
                    </h1>
                    <p className={styles.subtitle}>{t("trashSubtitle")}</p>
                </div>
                <div className={styles.headerRight}>
                    <span className={styles.summary}>
                        {t("nItems", { count: counts.items })} · {t("nProjects", { count: counts.projects })}
                    </span>
                    <button
                        type="button"
                        className={styles.emptyBtn}
                        disabled={counts.items === 0 && counts.projects === 0}
                        onClick={() => setEmptyOpen(true)}
                    >
                        {t("emptyTrash")}
                    </button>
                </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.toolbar}>
                <div className={styles.search}>
                    <Icon name="ic-search" className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        type="text"
                        placeholder={t("trashSearchPlaceholder")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                <div className={styles.pills}>
                    {(["all", "item", "project"] as KindFilter[]).map((k) => (
                        <button
                            key={k}
                            type="button"
                            className={`${styles.pill} ${kindFilter === k ? styles.pillActive : ""}`}
                            onClick={() => {
                                setKindFilter(k);
                                setPage(1);
                            }}
                        >
                            {k === "all" ? t("filterAll") : k === "item" ? t("filterItems") : t("filterProjects")}
                        </button>
                    ))}
                </div>
            </div>

            {entries.length === 0 ? (
                <div className={styles.emptyState}>{t("trashEmpty")}</div>
            ) : (
                <div className={styles.list}>{entries.map(renderRow)}</div>
            )}

            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

            {playing?.audioUrl && (
                <div className={audioFooter.section}>
                    <AudioPlayerRewind
                        title={playing.name}
                        src={playing.audioUrl}
                        isAutoPlay={true}
                        onClickAddToProject={() => {}}
                        showAddtoProject={false}
                    />
                </div>
            )}

            {textPreview && (
                <TextModal
                    isOpen={!!textPreview}
                    onClose={() => setTextPreview(null)}
                    title={textPreview.name}
                    content={textPreview.text || ""}
                />
            )}

            <DeleteModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handlePermanentDelete}
                title={t("permanentDelete")}
                message={
                    deleteTarget?.kind === "project"
                        ? t("permanentDeleteProjectConfirmation")
                        : t("permanentDeleteConfirmation")
                }
                requireTextConfirmation
            />

            <DeleteModal
                isOpen={emptyOpen}
                onClose={() => setEmptyOpen(false)}
                onConfirm={handleEmptyTrash}
                title={t("emptyTrash")}
                message={t("emptyTrashConfirmation")}
                requireTextConfirmation
            />
        </div>
    );
};

export default Trash;
